<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="(!isLoading && handedOverAllocation) || !route.query.swapToId"
    class="flex grow flex-col items-center justify-center"
  >
    <div class="w-full bg-white p-4 md:p-6">
      <ScaledContent>
        <div class="w-[210mm]" v-html="protocolHtml"></div>
      </ScaledContent>
    </div>
    <div class="w-full bg-white pb-8 pl-4 pr-4 md:pl-6 md:pr-6">
      <div v-if="handedOverAllocation" class="mb-4 space-y-5">
        <Checkbox v-model="iTakePicturesConfirmed" :label="t('confirmITakePictures')" />
        <Checkbox
          v-if="isSportsCar"
          v-model="noRacetracksConfirmed"
          :label="t('confirmNoRacetracks')"
        />
        <Checkbox
          v-for="(customCheckbox, index) in customCheckboxes"
          :key="customCheckbox"
          :label="customCheckbox"
          @change="customCheckboxesConfirmed[index] = $event"
        />
      </div>

      <div
        class="-mx-6 -mt-10 grid overflow-clip px-6 pb-10 pt-10 md:flex md:items-end md:justify-between"
      >
        <div class="">
          <div
            v-date="{
              value: DateTime.now().toISO(),
              format: DateTime.DATE_FULL,
            }"
            class="border-b border-black pb-2"
          ></div>
          <p class="mt-1">{{ t('date') }}</p>
        </div>
        <div class="max-w-[450px] flex-1">
          <div class="relative flex h-[140px] w-full items-stretch border-b border-black">
            <div
              v-if="signingState === SigningState.NOTREADY"
              class="flex h-full w-full items-center bg-gray-200 p-2 font-medium text-gray-500"
            >
              <span class="text-center">
                {{ t('pleaseConfirm') }}
              </span>
            </div>
            <canvas
              v-show="signingState === SigningState.ACTIVE || signingState === SigningState.FILLED"
              ref="signatureCanvasRef"
              class="bg-red absolute ml-[-70px] mr-[-70px] mt-[-80px] h-[340px] w-[560px]"
              width="560"
              height="340"
            ></canvas>
          </div>
          <p class="mt-1 text-right">{{ t('signature') }}</p>
        </div>
      </div>
      <p class="text-center text-xs">
        {{ t('imprint') }}
      </p>
    </div>

    <div ref="buttonRow" class="card-actions mt-8 grid w-full gap-4 md:flex md:justify-end">
      <CVButton
        v-show="!isSaving && signingState !== SigningState.NOTREADY"
        variant="ghost"
        @click="clearSignature"
      >
        {{ t('resetSignature') }}
      </CVButton>
      <CVButton
        variant="default"
        :disabled="signingState !== SigningState.FILLED"
        :is-loading="isSaving"
        @click="saveSignature"
      >
        {{ completeLabel }}
      </CVButton>
    </div>
  </div>
  <div v-else class="flex items-center justify-center py-10">
    <Spinner />
  </div>
</template>

<script lang="ts" setup>
import { VehicleCategory } from '@/entities/vehicle-category.enum';
import { useI18n } from 'vue-i18n';
import { useCarSwapProtocolTemplate, useCompleteHandover } from '@/queries/use-bookings';
import { useUploadContractSignature } from '@/queries/use-upload';
import { DateTime } from 'luxon';
import { create as createDrawingBoard } from 'simple-drawing-board';
import { computed, nextTick, ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import router from '@/router';
import { QueryLocale } from '@/entities/auth/local.enum';
import { getLocalizedField } from '@/utils/get-localized-field';
import Checkbox from '@/components/Checkbox.vue';
import ScaledContent from '@/components/ScaledContent.vue';
import { usePhoneLandscapeMode } from '@/hooks/use-phone-landscape-mode';
import { useAllocation } from '@/queries/use-allocations.ts';

enum SigningState {
  NOTREADY = 'NOTREADY',
  ACTIVE = 'ACTIVE',
  FILLED = 'FILLED',
}

const { t, locale } = useI18n();
const route = useRoute();
const signingState = ref(SigningState.NOTREADY);
const signatureCanvasRef = ref<HTMLCanvasElement>();
const drawingBoard = ref();
const buttonRow = ref<HTMLDivElement>();
const isSubmitting = ref(false);
const iTakePicturesConfirmed = ref(false);
const noRacetracksConfirmed = ref(false);
const customCheckboxesConfirmed = ref<boolean[]>([]);
const bookingId = route.params.bookingId as string;
const returnedAllocationId = route.query.swapFromId as string;
const handedOverAllocationId = route.query.swapToId as string;

const { data: handedOverAllocation } = useAllocation(
  handedOverAllocationId,
  () => !!route.query.swapToId,
);

const { isLoading, data: protocolHtml } = useCarSwapProtocolTemplate(
  bookingId,
  computed(() => ({
    returnedAllocationId,
    handedOverAllocationId,
    lang: locale.value as QueryLocale,
  })),
);

const { mutateAsync: completeHandover, isPending: isCompletingHandover } = useCompleteHandover();
const { mutateAsync: uploadSignature, isPending: isUploadingSignature } =
  useUploadContractSignature();

const isSaving = computed(
  () => isSubmitting.value || isCompletingHandover.value || isUploadingSignature.value,
);

usePhoneLandscapeMode();

const completeLabel = computed(() => {
  if (returnedAllocationId && !handedOverAllocationId) return t('completeReturnOnly');
  if (!returnedAllocationId && handedOverAllocationId) return t('completeHandoverOnly');
  return t('completeSwap');
});

watchEffect(() => {
  const allConfirmed =
    !route.query.swapToId ||
    (iTakePicturesConfirmed.value &&
      (noRacetracksConfirmed.value || !isSportsCar.value) &&
      customCheckboxesConfirmed.value.filter((confirmed) => confirmed).length ===
        customCheckboxes.value.length);
  if (allConfirmed) {
    if (signingState.value === SigningState.NOTREADY) {
      signingState.value = SigningState.ACTIVE;
    }
  } else {
    signingState.value = SigningState.NOTREADY;
  }
});

watch(
  [signingState, signatureCanvasRef],
  ([newVal]) => {
    if (!signatureCanvasRef.value) return;
    switch (newVal) {
      case SigningState.NOTREADY:
        drawingBoard.value?.clear();
        break;
      case SigningState.ACTIVE:
        drawingBoard.value = createDrawingBoard(signatureCanvasRef.value);
        drawingBoard.value.observer.on('drawEnd', () => {
          signingState.value = SigningState.FILLED;
        });
        // Scroll the action buttons into view when signing starts
        nextTick(() => {
          if (!buttonRow.value) return;
          const rect = buttonRow.value.getBoundingClientRect();
          const scrollingElement = document.scrollingElement || document.documentElement;
          const currentY = window.pageYOffset || scrollingElement.scrollTop || 0;
          const maxY = (scrollingElement.scrollHeight || 0) - window.innerHeight;
          const desiredY = currentY + rect.bottom - window.innerHeight + 30;
          const targetY = Math.max(0, Math.min(maxY, desiredY));
          if (targetY > currentY + 1) {
            window.scrollTo({ top: targetY, behavior: 'smooth' });
          }
        });
        break;
    }
  },
  { immediate: true },
);

const isSportsCar = computed(() => {
  const matchTypes = [VehicleCategory.SPORTS_CAR, VehicleCategory.SUPER_CAR];
  return matchTypes.some((i) =>
    handedOverAllocation.value?.car.vehicleType?.categories.includes(i),
  );
});

const customCheckboxes = computed(() => {
  return handedOverAllocation.value
    ? handedOverAllocation.value.car.vehicleType.customContractCheckboxes.map((checkbox) =>
        getLocalizedField(checkbox.label, locale.value),
      )
    : [];
});

const clearSignature = () => {
  if (!drawingBoard.value || isSaving.value) return;
  drawingBoard.value.clear();
  signingState.value = SigningState.ACTIVE;
};

const getSignatureBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve));

const saveSignature = async () => {
  if (!signatureCanvasRef.value || signingState.value !== SigningState.FILLED || isSaving.value) {
    return;
  }

  isSubmitting.value = true;
  try {
    const blob = await getSignatureBlob(signatureCanvasRef.value);
    if (!blob || (handedOverAllocationId && !handedOverAllocation.value)) {
      return;
    }
    const res = await uploadSignature({
      file: blob,
      params: {
        bookingId,
        handedOverAllocationId,
        returnedAllocationId,
      },
    });

    await completeHandover({
      bookingId,
      data: {
        createCarSwapProtocol: true,
        signatureFileKey: res.fileKey,
        returnedAllocationId,
        handedOverAllocationId,
      },
    });

    await router.push({
      name: 'swapDone',
      params: {
        bookingId,
      },
      query: {
        swapFromId: returnedAllocationId,
        swapToId: handedOverAllocationId,
      },
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
h3 {
  @apply text-sm font-medium xl:text-base;
}
h4 {
  @apply text-xs font-medium xl:text-sm;
}
</style>

<i18n lang="json">
{
  "en": {
    "pleaseConfirm": "Please confirm the required declarations first",
    "confirmITakePictures": "I take my own pictures of the vehicle and in particular the rims, the front bumper and the rear bumper to provide as evidence in the event of damage.",
    "confirmNoRacetracks": "I do not drive the rental car on race tracks.",
    "confirmNoRacetracksSuperCar": "I do not drive the rental car on race tracks. In case of breach, I acknowledge a contract penalty of 15,000€.",
    "date": "Date",
    "resetSignature": "Reset Signature",
    "completeSwap": "Complete car swap and send protocol",
    "completeReturnOnly": "Complete return and send protocol",
    "completeHandoverOnly": "Complete handover and send protocol",
    "signature": "Signature of renter/CarVia",
    "imprint": "Place of business: Munich, Germany • Commercial register: District court Munich: HRB 232676 • Managing directors: Joschka Reik, Julian Reik VAT-ID: DE312142017"
  },
  "de": {
    "pleaseConfirm": "Bitte bestätigen Sie zuerst die erforderlichen Erklärungen",
    "confirmITakePictures": "Ich mache eigene Bilder vom Fahrzeug und insbesondere den Felgen, der Frontsstoßstange und der Heckstoßstange, um sie im Schadensfall zum Beweis bereitzustellen.",
    "confirmNoRacetracks": "Ich fahre mit dem Mietwagen nicht auf Rennstrecken.",
    "confirmNoRacetracksSuperCar": "Ich fahre mit dem Wagen nicht auf Rennstrecken. Bei Missachtung erkenne ich eine Vertragsstrafe von 15.000€ an.",
    "date": "Datum",
    "resetSignature": "Unterschrift zurücksetzen",
    "completeSwap": "Autotausch abschließen und Protokoll senden",
    "completeReturnOnly": "Rückgabe abschließen und Protokoll senden",
    "completeHandoverOnly": "Übergabe abschließen und Protokoll senden",
    "signature": "Unterschrift Mieter/CarVia",
    "imprint": "Sitz der Gesellschaft: München • Handelsregister München: HRB 232676 • Geschäftsführer: Joschka Reik, Julian Reik • Ust.-ID: DE312142017"
  },
  "es": {
    "pleaseConfirm": "Confirme primero las declaraciones obligatorias",
    "confirmITakePictures": "Tomo mis propias fotos del vehículo y en particular de las llantas, el paragolpes delantero y el trasero para aportarlas como prueba en caso de daños.",
    "confirmNoRacetracks": "No conduzco el vehículo de alquiler en circuitos.",
    "confirmNoRacetracksSuperCar": "No conduzco el vehículo de alquiler en circuitos. En caso de incumplimiento, acepto una penalización contractual de 15.000 €.",
    "date": "Fecha",
    "resetSignature": "Restablecer firma",
    "completeSwap": "Completar cambio de vehículo y enviar protocolo",
    "completeReturnOnly": "Completar devolución y enviar protocolo",
    "completeHandoverOnly": "Completar entrega y enviar protocolo",
    "signature": "Firma del arrendatario / CarVia",
    "imprint": "Sede: Múnich, Alemania • Registro mercantil: Tribunal de Múnich HRB 232676 • Directores: Joschka Reik, Julian Reik • NIF-IVA: DE312142017"
  }
}
</i18n>
